import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useQuery } from "@apollo/client";
import { useRouter } from "next/router";
import clsx from "clsx";
import dynamic from "next/dynamic";

import AddToPlaylistModal from "@/components/playlists/AddToPlaylistModal";
import LoadingIndicator from "@/components/ui/svg/LoadingIndicator";
import { SelectedPerks } from "@/components/profile";
import { getCurrencyCode } from "@/utils/currencies";
import { useWindowSize } from "@/components/WindowSizeProvider";
import {
  Currency,
  DetailedPrivateUserFragment,
  GetUserNftsDocument,
  NftSort,
  PlaylistSongNftFragment,
  Rarity,
  ReleaseSort,
} from "@/graphql/types";
import Link from "@/components/hoc/CustomLink";

const CollectionCard = dynamic(() => import("@/components/profile/collection/CollectionCard"));

const PAGE_LIMIT = 12;

interface Props {
  me: DetailedPrivateUserFragment | undefined;
  user: { id: string; username: string } | undefined;
  searchQuery: string | undefined;
  scarcityFilter: Rarity | undefined;
  onShowPerks: (perks: SelectedPerks) => void;
}

export default function CollectionSection({ me, user, searchQuery, scarcityFilter, onShowPerks }: Props) {
  const { width } = useWindowSize();
  const isMobile = width < 1024;
  const { t } = useTranslation("profile");
  const router = useRouter();

  let page = router.query.page
    ? +(typeof router.query.page === "object" ? router.query.page[0] : router.query.page) - 1
    : 0;
  if (Number.isNaN(page) || page < 0) page = 0;

  const [songToAddToPlaylist, setSongToAddToPlaylist] = useState<PlaylistSongNftFragment>();

  const { data, loading, refetch } = useQuery(GetUserNftsDocument, {
    skip: !user?.id,
    variables: {
      ownerId: user?.id ?? "skip",
      currency: getCurrencyCode() as string as Currency,
      offset: page * PAGE_LIMIT,
      limit: PAGE_LIMIT,
      nftsLimit: 1,
      rarity: scarcityFilter ? [scarcityFilter] : undefined,
      sort: ReleaseSort.ReleaseDateDec,
      nftsSort: NftSort.LastPriceInc,
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    refetch({ searchQuery });
  }, [searchQuery, refetch]);

  useEffect(() => {
    const offset = page * PAGE_LIMIT;
    refetch({ offset });
  }, [page, refetch]);

  const pageCount = Math.ceil((data?.releases.size ?? 0) / PAGE_LIMIT);

  if (loading && !data?.releases.results.length)
    return (
      <div className="flex justify-center mt-14 text-purple">
        <LoadingIndicator width="30" height="30" />
      </div>
    );

  if (data && data?.releases.results.length > 0 && user?.id) {
    return (
      <div className="flex flex-col w-full">
        <div
          className={clsx(
            "w-full grid grid-cols-1 sm:grid-cols-2 2lg:grid-cols-3 2xl:grid-cols-4 2xl:max-w-[1280px]",
            "gap-4 sm:gap-6 lg:gap-[5vw] 2lg:gap-[2.5vw] 2xl:gap-[min(1.5vw,48px)]",
            loading && "animate-pulse"
          )}
        >
          <AddToPlaylistModal
            song={songToAddToPlaylist}
            show={!!songToAddToPlaylist}
            onClose={() => setSongToAddToPlaylist(undefined)}
          />

          {data?.releases.results.map((release, i) => {
            return (
              <div className={"w-full relative lg:[perspective:1000px]"} key={release.id}>
                <CollectionCard
                  mine={!!me && me?.id === user?.id}
                  ownerId={user?.id}
                  {...{ release }}
                  playlist={data?.releases.results.map((r) => r.track)}
                  playlistIndex={i}
                  onAddToPlaylist={(song) => setSongToAddToPlaylist(song)}
                  onShowPerks={() => {
                    const nft = release.nfts?.results[0];
                    onShowPerks({
                      track: release.track,
                      scarcities: [release.rarity],
                      sale: nft ? { release, nft } : undefined,
                    });
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* MARK: Pagination */}
        {pageCount > 1 && (
          <ReactPaginate
            initialPage={0}
            onPageChange={(e) => {
              router.push(
                {
                  pathname: router.pathname,
                  query: { ...router.query, page: e.selected + 1 },
                },
                undefined,
                { shallow: true }
              );
            }}
            disableInitialCallback
            pageRangeDisplayed={isMobile ? 2 : 4}
            marginPagesDisplayed={1}
            pageCount={pageCount}
            forcePage={page}
            containerClassName={clsx(
              "mt-8 lg:mt-10 relative flex flex-row items-center",
              "justify-center lg:justify-start text-base leading-1 -mx-1"
            )}
            pageClassName={clsx(
              "group min-w-10 h-10 p-0.5 mx-1 rounded-full bg-dark-8/10",
              "border border-purple-4 md:hover:shadow-small",
              "md:hover:border-none cursor-pointer relative",
              "hover:before:border-[2.5px] hover:before:bg-gradient-selected-border",
              "hover:before:border-gradient hover:before:rounded-full"
            )}
            pageLinkClassName={clsx("w-full h-full flex items-center justify-center")}
            activeClassName={clsx(
              "shadow-small text-white border-none",
              "bg-gradient-selected pointer-events-none"
            )}
            breakLabel="..."
            breakClassName="mx-1"
            nextLabel={null}
            previousLabel={null}
          />
        )}
      </div>
    );
  }

  function geti18nContext() {
    if (searchQuery) return "search";
    else return user?.id === me?.id ? "me" : "them";
  }

  return (
    <div className="flex flex-col items-center text-center text-lg leading-1.2 mt-14">
      <span className="mb-6">
        {t(`profile.collection.none.text_${geti18nContext()}`, {
          search: searchQuery,
          user: user?.username,
        })}
      </span>

      {user?.id === me?.id && (
        <Link href="/market">
          <a
            className={clsx(
              "min-w-max w-max flex items-center relative",
              "h-8 lg:h-10 px-3 lg:px-5 rounded-full bg-purple",
              "transition ease-quart-out duration-300 md:hover:shadow-button",
              "tracking-wider uppercase font-medium",
              "text-xs leading-1 lg:text-sm text-white",
              "border-2 border-purple md:hover:bg-transparent md:hover:text-purple"
            )}
          >
            {t("profile.collection.none.cta")}
          </a>
        </Link>
      )}
    </div>
  );
}
